import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Steps, Modal, Spin } from 'antd';
import { CustomImage, CustomPageHeader, Text, useText } from '@src/components';
import StepContent from './StepContent';
import iconFinish from '@src/assets/icons/icon-done.svg';
import {
  setActiveStep,
  finishLoadingStep,
  resetCurrentLoadAddNewCarType,
} from '@src/store/slice/addNewCarStore/addCarTypeSlice';
import {
  getVehicleAttributes,
  getVehicleCostCenter,
  getVehicleDepartments,
  getVehicleFuelTypes,
  getVehicleLAddressGetList,
  getVehicleLimitTypes,
  getVehicleLimitTypesDayRestrictionOperations,
  getVehicleLimitValueTypes,
  getVehicleTypes,
  resetCurrentLoadAddCarPlateDetail,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import { useLanguageChange } from '@src/hooks/useLanguageChange';
import '@src/styles/addNewCar/addNewCar.scss';
import { useHistory } from 'react-router-dom';
import { resetCurrentLoadAddCarPlateInformation } from '@src/store/slice/addNewCarStore/addCarPlateInformationSlice';
import { setShowLoader } from '@src/store/slice/appSettingsSlice';

const { Step } = Steps;
const { confirm } = Modal;

const steps = [
  {
    title: <Text t="carOrderInformation" />,
    content: 'addNewCarContainer',
    key: 'addCarType',
  },
  {
    title: <Text t="carInformation" />,
    content: 'addNewCarContainer',
    key: 'addCarPlateInformation',
  },
  {
    title: <Text t="vehicleDetails" />,
    content: 'addNewCarContainer',
    key: 'addCarPlateDetail',
  },
  {
    title: <Text t="orderSummary" />,
    content: 'addNewCarContainer',
    key: 'addNewCarOrderSummary',
  },
  {
    title: <Text t="approval" />,
    content: 'addNewCarContainer',
    key: 'addNewCarApproval',
  },
];

function getProgressDot() {
  return (iconDot, { index, status }) =>
    status === 'finish' ? <CustomImage src={iconFinish} /> : index + 1;
}

const AddNewCar = (props) => {
  const dispatch = useDispatch();
  const { activeStep, selectVehicleDeviceType, selectVehicleProviderType, loading } = useSelector(
    (state) => state.addCarType,
  );
  const { allPlatesArray, allPlatesDetails } = useSelector(
    (state) => state?.addCarPlateInformation,
  );
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const history = useHistory();
  const [disableTicketCarPage, setDisableTicketCarPage] = useState(true);
  const { menus } = useSelector((state) => state.menus);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { showLoader } = useSelector((state) => state?.appSettings);
  const [flagDepartments, setFlagDepartments] = useState(false);
  const [flagCostCenter, setFlagCostCenter] = useState(false);
  const [flagAddressList, setFlagAddressList] = useState(false);

  const areYouSureYouWantToGoBack = useText('areYouSureYouWantToGoBack');
  const ifYouPerformThisTransactionYourCurrentInformation2 = useText(
    'ifYouPerformThisTransactionYourCurrentInformation2',
  );
  const yesText = useText('yes');
  const noText = useText('no');

  const {
    vehicleFuelTypes = [],
    vehicleDepartments = [],
    vehicleCostCenters = [],
    vehicleTypes = [],
    vehicleAttributes = [],
    vehicleLimitTypes = [],
    vehicleLimitTypesDayRestrictionOperations = [],
    vehicleLimitValueTypes = [],
    vehicleLAddressGetList = [],
  } = useSelector((state) => state.addCarPlateDetail);

  const customerId = isAdmin && selectedUserId ? selectedUserId : selectedCustomer?.customerId;
  // Shared by the two admin-aware lookups below (vouId is the only thing that differs between them).
  const adminAwareVouParams = (vouId) =>
    isAdmin && selectedUserId ? { customerId: selectedUserId, vouId } : { vouId };
  const addressParams =
    isAdmin && selectedUserId
      ? { notUnique: true, customerId: selectedUserId }
      : { notUnique: true };

  /**
   * The lookup lists feeding this wizard's selects (vehicle type/qualification/fuel type, limit
   * types...) carry backend generated labels, and every effect below only fetches them while they
   * are still empty. Without this they would keep their old language until a manual page refresh,
   * so re-request the ones that are already loaded whenever the language is switched. Selections
   * are kept, since only the labels change - the ids stay the same.
   */
  useLanguageChange(() => {
    const refetchers = [
      [vehicleDepartments, () => getVehicleDepartments(adminAwareVouParams(4))],
      [vehicleCostCenters, () => getVehicleCostCenter(adminAwareVouParams(4))],
      [vehicleLAddressGetList, () => getVehicleLAddressGetList(addressParams)],
      [vehicleLimitValueTypes, () => getVehicleLimitValueTypes({ custId: customerId })],
      [
        vehicleLimitTypes,
        () => getVehicleLimitTypes({ custId: customerId, vouId: selectVehicleProviderType }),
      ],
      [vehicleAttributes, () => getVehicleAttributes()],
      [vehicleTypes, () => getVehicleTypes()],
      [vehicleFuelTypes, () => getVehicleFuelTypes()],
      [
        vehicleLimitTypesDayRestrictionOperations,
        () => getVehicleLimitTypesDayRestrictionOperations({ vouId: 4 }),
      ],
    ];
    refetchers
      .filter(([list]) => list?.length)
      .forEach(([, buildAction]) => dispatch(buildAction()));
  });

  const fetchData = async () => {
    if (isAdmin && selectedUserId) {
      await dispatch(getVehicleDepartments({ customerId: selectedUserId, vouId: 4 }));
    } else {
      await dispatch(getVehicleDepartments({ vouId: 4 }));
    }
  };

  useEffect(async () => {
    if (
      showLoader &&
      vehicleAttributes?.length > 0 &&
      vehicleLimitTypesDayRestrictionOperations?.length > 0 &&
      vehicleFuelTypes?.length > 0 &&
      vehicleTypes?.length > 0 &&
      vehicleLimitValueTypes?.length > 0 &&
      (vehicleLAddressGetList?.length > 0 || flagAddressList) &&
      (vehicleCostCenters?.length > 0 || flagCostCenter) &&
      (vehicleDepartments?.length > 0 || flagDepartments)
    ) {
      await dispatch(setShowLoader(false));
    }
  }, [
    showLoader,
    vehicleLAddressGetList,
    vehicleAttributes,
    vehicleDepartments,
    vehicleLimitTypesDayRestrictionOperations,
    vehicleFuelTypes,
    vehicleCostCenters,
    vehicleTypes,
    vehicleLimitValueTypes,
    flagAddressList,
    flagDepartments,
    flagCostCenter,
    dispatch,
  ]);

  useEffect(async () => {
    if (vehicleDepartments?.length === 0 && !flagDepartments) {
      await dispatch(setShowLoader(true));
      await fetchData();
      setFlagDepartments(true);
    } else if (vehicleDepartments?.length > 0) {
      setFlagDepartments(false);
    }
  }, [vehicleDepartments, dispatch]);

  useEffect(async () => {
    if (vehicleLimitValueTypes?.length === 0) {
      await dispatch(setShowLoader(true));
      if (isAdmin && selectedUserId) {
        await dispatch(
          getVehicleLimitValueTypes({
            custId: selectedUserId,
          }),
        );
      } else {
        await dispatch(
          getVehicleLimitValueTypes({
            custId: selectedCustomer.customerId,
          }),
        );
      }
    }
  }, [vehicleLimitValueTypes, dispatch]);

  useEffect(async () => {
    if (vehicleCostCenters?.length === 0 && !flagCostCenter) {
      await dispatch(setShowLoader(true));
      if (isAdmin && selectedUserId) {
        await dispatch(getVehicleCostCenter({ vouId: 4, customerId: selectedUserId }));
      } else {
        await dispatch(getVehicleCostCenter({ vouId: 4 }));
      }
      setFlagCostCenter(true);
    } else if (vehicleCostCenters?.length > 0) {
      setFlagCostCenter(false);
    }
  }, [vehicleCostCenters, dispatch]);

  useEffect(async () => {
    if (vehicleLAddressGetList?.length === 0 && !flagAddressList) {
      await dispatch(setShowLoader(true));
      if (isAdmin && selectedUserId) {
        await dispatch(
          getVehicleLAddressGetList({
            notUnique: true,
            customerId: selectedUserId,
          }),
        );
      } else {
        await dispatch(getVehicleLAddressGetList({ notUnique: true }));
      }
      setFlagAddressList(true);
    } else if (vehicleLAddressGetList?.length > 0) {
      setFlagAddressList(false);
    }
  }, [vehicleLAddressGetList, dispatch]);

  useEffect(async () => {
    if (vehicleAttributes?.length === 0) {
      await dispatch(setShowLoader(true));
      await dispatch(getVehicleAttributes());
    }
  }, [vehicleAttributes, dispatch]);

  useEffect(async () => {
    if (vehicleTypes?.length === 0) {
      await dispatch(getVehicleTypes());
    }
  }, [vehicleTypes, dispatch]);

  useEffect(async () => {
    if (vehicleFuelTypes?.length === 0) {
      await dispatch(getVehicleFuelTypes());
    }
  }, [vehicleFuelTypes, dispatch]);

  useEffect(async () => {
    if (vehicleLimitTypesDayRestrictionOperations?.length === 0) {
      await dispatch(getVehicleLimitTypesDayRestrictionOperations({ vouId: 4 }));
    }
  }, [vehicleLimitTypesDayRestrictionOperations, dispatch]);

  useEffect(() => {
    if (loading.activeStep) {
      const timeoutId = setTimeout(() => {
        dispatch(finishLoadingStep());
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, activeStep, loading.activeStep]);

  useEffect(() => {
    if (menus && !isAdmin) {
      if (menus.filter((item) => item.text?.includes('TICKET CAR')).length < 1) {
        setDisableTicketCarPage(false);
      }
    }
  }, [menus]);

  useEffect(() => {
    if (!disableTicketCarPage && history) {
      history.push('/dashboard');
    }
  }, [disableTicketCarPage]);

  const resetStepsAndForm = () => {
    dispatch(resetCurrentLoadAddNewCarType());
    dispatch(resetCurrentLoadAddCarPlateDetail());
    dispatch(setActiveStep(0));
  };

  useEffect(() => {
    if (history) {
      const unlisten = history.listen(() => {
        resetStepsAndForm();
      });

      return () => {
        unlisten();
      };
    }
  }, [history]);

  const showConfirm = (current) => {
    confirm({
      title: areYouSureYouWantToGoBack,
      content: ifYouPerformThisTransactionYourCurrentInformation2,
      okText: yesText,
      cancelText: noText,
      centered: true,
      onOk() {
        if (current === 0) {
          dispatch(resetCurrentLoadAddNewCarType());
          dispatch(resetCurrentLoadAddCarPlateDetail());
          dispatch(resetCurrentLoadAddCarPlateInformation());
          dispatch(setActiveStep(current));
        } else if (current === 1 || current === 2) {
          dispatch(resetCurrentLoadAddCarPlateDetail());
          dispatch(setActiveStep(current));
        } else if (selectVehicleDeviceType !== null) {
          dispatch(setActiveStep(current));
        }
      },
      onCancel() {},
    });
  };

  const handleStepClick = (current) => {
    if (current < activeStep) {
      if (allPlatesDetails?.length > 0 && allPlatesArray && activeStep === 3 && current === 2) {
        dispatch(setActiveStep(current));
      } else {
        showConfirm(current);
      }
    }
  };

  return (
    <CustomPageHeader
      data-testid="add-new-car-step"
      title={isAdmin ? null : <Text t="addCar" />}
      showBreadCrumb={!isAdmin}
      showHelpButton={!isAdmin}
      adminCustomHeaderContent={isAdmin ? 'custom-header-content-subpage' : ''}
      routes={['ticketCarHeader']}
    >
      <div className="row">
        <Steps
          current={activeStep}
          className="card-order-steps"
          labelPlacement="vertical"
          progressDot={getProgressDot()}
          onChange={handleStepClick}
        >
          {steps.map((item, index) => (
            <Step
              key={item.key}
              title={item.title}
              disabled={activeStep === 4 && index !== activeStep}
            />
          ))}
        </Steps>
      </div>
      {loading.activeStep ? (
        <div className="loading-container" style={{ textAlign: 'center', padding: '200px' }}>
          <Spin size="large" className="custom-spin" />
        </div>
      ) : (
        <StepContent activeStep={activeStep} />
      )}
    </CustomPageHeader>
  );
};

export default AddNewCar;
